import React, {useMemo, useState} from 'react';
import useOnNewRequest from '../../../../utility/hooks/useOnNewRequest';
import {
    Box,
    CircularProgress,
    Stack,
    Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import IconButton from '@mui/material/IconButton';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import {useGetGroupFiltersQuery} from '../../../../api/services/groupService';
import {
    useDeleteStudentProfileAccessRestrictionRuleMutation,
    useGetStudentProfileAccessRestrictionRulesQuery,
    useLazyGetStudentProfileAccessRestrictionRuleQuery,
    useUpsertStudentProfileAccessRestrictionRuleMutation
} from '../../../../api/services/schoolManagementService';
import useNotification from '../../../../utility/hooks/useNotification';
import StudentProfileAccessRestrictionDialog from './StudentProfileAccessRestrictionDialog';

/* eslint-disable react/prop-types */

// Maria, 19 October 2026: the restrictions under the 'Restrict Access' button, with the 'Add Restriction' button
// on the same row as the Manage Access buttons and the list in the same style as the Manage Access table
const StudentProfileAccessRestrictions = ({addRequest}) => {
    const {showSuccessNotification, showErrorNotification} = useNotification();
    const {
        data: rules = [],
        isLoading: isLoadingRules
    } = useGetStudentProfileAccessRestrictionRulesQuery();
    const {data: groupFiltersResponse = {modules: []}} = useGetGroupFiltersQuery({});
    const [fetchRule] = useLazyGetStudentProfileAccessRestrictionRuleQuery();
    const [upsertRule, {isLoading: isSaving}] = useUpsertStudentProfileAccessRestrictionRuleMutation();
    const [deleteRule, {isLoading: isDeleting}] = useDeleteStudentProfileAccessRestrictionRuleMutation();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [editingRuleId, setEditingRuleId] = useState(null);
    const [dialogInitialValues, setDialogInitialValues] = useState(null);
    const [isLoadingRule, setIsLoadingRule] = useState(false);
    const [rulePendingDelete, setRulePendingDelete] = useState(null);

    const ruleColumns = useMemo(() => [
        {
            accessorKey: 'subjectLabel',
            header: 'Restriction for',
            Cell: ({row}) => (
                <Typography>
                    {row.original.subjectLabel}
                    <Typography component="span" color="text.secondary" sx={{ml: 1}}>
                        ({row.original.subjectType === 'employee' ? 'Member of staff' : 'Group of staff'})
                    </Typography>
                </Typography>
            )
        },
        {
            accessorKey: 'studentGroupCount',
            header: 'Excluded student groups'
        },
        {
            accessorKey: 'studentCount',
            header: 'Excluded students and applicants'
        }
    ], []);

    const handleOpenCreate = () => {
        setDialogMode('create');
        setEditingRuleId(null);
        setDialogInitialValues(null);
        setDialogOpen(true);
    };

    useOnNewRequest(addRequest, handleOpenCreate);

    const handleOpenEdit = async (ruleId) => {
        setDialogMode('edit');
        setEditingRuleId(ruleId);
        setDialogOpen(true);
        setIsLoadingRule(true);
        setDialogInitialValues(null);

        try {
            const rule = await fetchRule(ruleId).unwrap();
            setDialogInitialValues({
                subjectType: rule.subjectType,
                employee: rule.employeeId
                    ? {
                        id: rule.employeeId,
                        preferredForename: rule.preferredForename,
                        preferredSurname: rule.preferredSurname,
                        displayName: rule.employeeName
                    }
                    : null,
                employeeGroup: rule.employeeGroupFilterId
                    ? {
                        groupFilterId: rule.employeeGroupFilterId,
                        customGroupId: rule.employeeCustomGroupId,
                        name: rule.employeeGroupName
                    }
                    : null,
                students: (rule.students || [])
                    .filter(student => !student.isApplicant)
                    .map(student => ({
                        id: student.id,
                        label: student.label
                    })),
                applications: (rule.students || [])
                    .filter(student => student.isApplicant)
                    .map(student => ({
                        id: student.id,
                        label: student.label
                    })),
                studentGroups: (rule.studentGroups || []).map(group => ({
                    id: group.groupFilterId,
                    groupFilterId: group.groupFilterId,
                    name: group.name
                }))
            });
        } catch (e) {
            showErrorNotification('Failed to load restriction rule');
            setDialogOpen(false);
        } finally {
            setIsLoadingRule(false);
        }
    };

    const handleCloseDialog = () => {
        if (isSaving) {
            return;
        }
        setDialogOpen(false);
        setEditingRuleId(null);
        setDialogInitialValues(null);
    };

    const handleSave = async (payload) => {
        try {
            await upsertRule({
                ruleId: payload.ruleId,
                employeeId: payload.employeeId,
                employeeGroupFilterId: payload.employeeGroupFilterId,
                studentIds: payload.studentIds,
                studentGroupFilterIds: payload.studentGroupFilterIds
            }).unwrap();

            showSuccessNotification(
                dialogMode === 'edit'
                    ? 'Restriction updated'
                    : 'Restriction created'
            );
            handleCloseDialog();
        } catch (e) {
            showErrorNotification(
                e?.data?.detail ||
                e?.data?.title ||
                'Failed to save student profile access restriction'
            );
        }
    };

    const handleConfirmDelete = async () => {
        if (!rulePendingDelete) {
            return;
        }

        try {
            await deleteRule(rulePendingDelete.id).unwrap();
            showSuccessNotification('Restriction deleted');
            setRulePendingDelete(null);
            if (editingRuleId === rulePendingDelete.id) {
                handleCloseDialog();
            }
        } catch (e) {
            showErrorNotification('Failed to delete restriction rule');
        }
    };

    return (
        <>
            {isLoadingRules && (
                <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={28}/>
                </Box>
            )}
            {!isLoadingRules && (
                <AdmicityTableV2
                    columns={ruleColumns}
                    data={rules}
                    hideFooterPagination
                    tableProps={{
                        layoutMode: 'semantic',
                        enableRowActions: true,
                        positionActionsColumn: 'last',
                        enableSorting: false,
                        enableFilters: false,
                        enableHiding: false,
                        enableFullScreenToggle: false,
                        enableColumnResizing: false,
                        enableColumnActions: false,
                        enableBottomToolbar: false,
                        enableTopToolbar: false,
                        displayColumnDefOptions: {'mrt-row-actions': {header: 'Actions'}},
                        muiTableProps: {sx: {tableLayout: 'auto', width: '100%'}},
                        muiTablePaperProps: {sx: {flexGrow: 0}},
                        muiTableContainerProps: {sx: {flex: 'none', maxHeight: 'none'}},
                        muiTableHeadCellProps: {
                            sx: {
                                '&.MuiTableCell-root': {backgroundColor: '#e3f5f1', backgroundImage: 'none'},
                                fontWeight: 600
                            }
                        },
                        muiTableBodyCellProps: {sx: {py: 0.5}},
                        muiTableBodyRowProps: {sx: {height: 38}},
                        renderEmptyRowsFallback: () => (
                            <Typography sx={{px: 2, py: 1.5}} color="text.secondary">
                                No restrictions have been added.
                            </Typography>
                        ),
                        renderRowActions: ({row}) => (
                            <Stack direction="row" spacing={0.5}>
                                <IconButton
                                    size="small"
                                    aria-label="Edit restriction"
                                    onClick={() => handleOpenEdit(row.original.id)}
                                >
                                    <EditOutlinedIcon fontSize="small"/>
                                </IconButton>
                                <IconButton
                                    size="small"
                                    aria-label="Delete restriction"
                                    onClick={() => setRulePendingDelete(row.original)}
                                    disabled={isDeleting}
                                >
                                    <DeleteOutlineRoundedIcon fontSize="small"/>
                                </IconButton>
                            </Stack>
                        )
                    }}
                />
            )}

            <StudentProfileAccessRestrictionDialog
                open={dialogOpen}
                mode={dialogMode}
                ruleId={editingRuleId}
                initialValues={dialogInitialValues}
                existingRules={rules}
                groupFilterModules={groupFiltersResponse.modules || []}
                isLoading={isLoadingRule}
                isSaving={isSaving}
                onClose={handleCloseDialog}
                onSave={handleSave}
            />

            <AdmicityDialog
                open={Boolean(rulePendingDelete)}
                handleClose={() => setRulePendingDelete(null)}
                title="Delete restriction"
                contentText={
                    rulePendingDelete
                        ? `Delete the restriction for ${rulePendingDelete.subjectLabel}?`
                        : ''
                }
                actions={[
                    {
                        label: 'Cancel',
                        onClick: () => setRulePendingDelete(null),
                        disabled: isDeleting
                    },
                    {
                        label: isDeleting ? 'Deleting...' : 'Delete',
                        onClick: handleConfirmDelete,
                        disabled: isDeleting
                    }
                ]}
            />
        </>
    );
};

StudentProfileAccessRestrictions.propTypes = {
    addRequest: PropTypes.number
};

export default StudentProfileAccessRestrictions;
